import {Graphics, Polygon, Rectangle} from "pixi.js";
import {HardwareItem} from "../../../../../../api/Hardware";
import {lineBlueHeavy, lineBlueLight, lineRed} from "../../const";

// A category-driven hardware drawing: a generic picture of a product category (a rim exit
// device, a closer, a kick plate, ...) overlaid on the door or frame at the item's placement.
// Each glyph decides which items it draws (by category path) and which targets it can draw
// on; HardwareCrosshairThing asks the registry (see ./index.ts) for one per placed item and
// falls back to the lever / deadbolt / crosshair when none matches.
export type HardwareGlyph = {
    id: string;
    // which placement targets this glyph draws on. A placement on another target keeps the
    // default marker.
    targets: ("door" | "frame")[];
    matches(item: HardwareCategoryFields): boolean;
    // draw into `g` (already cleared) in the Thing's local coords and return the hit area.
    draw(g: Graphics, ctx: GlyphContext): Rectangle | Polygon;
    // the area the drawing covers, as rects in the same coords as draw, without any hit
    // padding. The lite-overlap check reads this (see model/hardware/MdHardwareFootprint).
    footprint(ctx: GlyphFootprintContext): GlyphRect[];
    // draw the item's profile as seen from the door's latch edge (the Locations view's
    // middle column), standing off the face it mounts on. Optional: an item without one
    // draws as a tick on the edge.
    drawEdge?(g: Graphics, ctx: EdgeGlyphContext): Rectangle;
};

export type HardwareCategoryFields = Pick<HardwareItem, "category" | "category2" | "category3">;

export type GlyphRect = {x0: number; y0: number; x1: number; y1: number};

// a door leaf as a frame glyph sees it: its box in the same coords as the glyph's rect,
// and which edge its hinges are on.
export type GlyphLeaf = {rect: GlyphRect; hingeSide: "left" | "right"};

// what fixes a glyph's geometry: the item, where it sits and what it sits on.
export type GlyphFootprintContext = {
    // the item being drawn, for glyphs that vary by the exact category.
    item: HardwareCategoryFields;
    // the placement centre (the crosshair hub) in local coords.
    hub: {x: number; y: number};
    // the target rect the placement sits in: the door leaf, or the frame stick.
    target: "door" | "frame";
    rect: GlyphRect;
    // which edge of `rect` the hinges are on. null for a frame placement.
    hingeSide: "left" | "right" | null;
    // the door leaves in the frame, for a frame glyph that reaches onto them (an operator
    // arm). Empty for a door placement, and for a frame placement whose host has no leaves.
    leaves: GlyphLeaf[];
};

export type GlyphContext = GlyphFootprintContext & {
    selected: boolean;
    // the placement breaks a placement rule (see model/hardware/MdHardwareRules); draw in red.
    invalid: boolean;
};

// the frame head in section over the latch edge: its extents across the edge (its two
// faces are the extremes), the top of the frame, the bottom of the face return, and the
// soffit (the rabbet bottom the leaf top seats under).
export type EdgeHead = {x0: number; x1: number; top: number; faceBottom: number; soffit: number};

export type EdgeGlyphContext = {
    item: HardwareCategoryFields;
    // the target the placement sits on: the leaf, or the frame head over it.
    target: "door" | "frame";
    // the point on the face the item mounts on, at the placement height, in local coords.
    // A head placement has no height on the leaf; its y is the leaf's top.
    face: {x: number; y: number};
    // the horizontal direction away from that face (out of the door).
    out: 1 | -1;
    // the door edge across the view (its two faces), for an item centred on the door
    // thickness.
    edge?: {x0: number; x1: number};
    // the frame head over the edge, when the view draws one, for an item that seats on
    // the frame face (an operator header) rather than the door face.
    head?: EdgeHead;
    selected: boolean;
    invalid: boolean;
};

// the stroke/fill colour + alpha a glyph draws with, matching the lever / deadbolt glyphs.
// A rule-breaking placement draws red at full strength so it stands out.
export function glyphStyle(selected: boolean, invalid: boolean = false): {color: number; alpha: number} {
    if(invalid) return {color: lineRed, alpha: 1};
    return {color: selected ? lineBlueLight : lineBlueHeavy, alpha: selected ? 1 : 0.5};
}

// the smallest rect around every rect in `rects`.
export function boundsOf(rects: GlyphRect[]): GlyphRect {
    return {
        x0: Math.min(...rects.map(r => r.x0)),
        y0: Math.min(...rects.map(r => r.y0)),
        x1: Math.max(...rects.map(r => r.x1)),
        y1: Math.max(...rects.map(r => r.y1)),
    };
}

// a footprint's hit area: its bounds, padded so a thin outline stays easy to click.
export function hitAreaOf(rects: GlyphRect[], pad: number): Rectangle {
    const b = boundsOf(rects);
    return new Rectangle(b.x0 - pad, b.y0 - pad, b.x1 - b.x0 + 2 * pad, b.y1 - b.y0 + 2 * pad);
}

// the rect spanning the given x and y positions, in either order.
export function spanRect(xs: number[], ys: number[]): GlyphRect {
    return {x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys)};
}

// whether any of the item's category path levels equals one of `names` (case-insensitive).
export function hasCategory(item: HardwareCategoryFields, ...names: string[]): boolean {
    const keys = names.map(n => n.toLowerCase());
    return [item.category, item.category2, item.category3]
        .some(c => !!c && keys.includes(c.toLowerCase()));
}

// the strike (latch) edge of the rect: the edge opposite the hinges. Falls back to the right
// edge when the hinge side is unknown.
export function strikeEdgeX(ctx: GlyphFootprintContext): {x: number; dir: 1 | -1} {
    if(ctx.hingeSide === "right") return {x: ctx.rect.x0, dir: 1};
    return {x: ctx.rect.x1, dir: -1};
}

// the hinge edge of the rect. Falls back to the left edge when the hinge side is unknown.
export function hingeEdgeX(ctx: GlyphFootprintContext): number {
    return ctx.hingeSide === "right" ? ctx.rect.x1 : ctx.rect.x0;
}
